/**
 * Ship-related sprites. Ship geometry itself is modular — see parts.ts.
 */
import * as THREE from "three";
import { strand } from "../ui/meridian";

/** Overhead chat bubble (spec §2): a LOCAL line floats above the speaker's
 *  name sprite for ~5–7 s, WoW-fashion. HUD-styled — same dark panel + light
 *  text as the name label. Chat is HOSTILE free text; canvas fillText is the
 *  only render path (no HTML anywhere near it). */
const BUB_W = 512;
const BUB_FONT = 26;
const BUB_LINE = 34;
const BUB_PAD = 18;
const BUB_MAX_LINES = 3;

/** Word-wrap into at most BUB_MAX_LINES; overflow ellipsises the last line. */
function wrapBubble(ctx: CanvasRenderingContext2D, text: string, maxW: number): string[] {
  const lines: string[] = [];
  let line = "";
  let overflow = false;
  outer: for (const word of text.split(/\s+/).filter((w) => w.length > 0)) {
    let w = word;
    while (w.length > 0) {
      const tryLine = line.length > 0 ? `${line} ${w}` : w;
      if (ctx.measureText(tryLine).width <= maxW) { line = tryLine; break; }
      if (line.length === 0) {
        // A single fragment wider than the bubble hard-splits (URLs, mash).
        let cut = w.length - 1;
        while (cut > 1 && ctx.measureText(w.slice(0, cut)).width > maxW) cut--;
        line = w.slice(0, cut);
        w = w.slice(cut);
      }
      if (lines.length === BUB_MAX_LINES - 1) { overflow = true; break outer; }
      lines.push(line);
      line = "";
    }
  }
  if (line.length > 0) lines.push(line);
  if (overflow && lines.length > 0) {
    const last = lines[lines.length - 1];
    let cut = last.length;
    while (cut > 0 && ctx.measureText(`${last.slice(0, cut)}…`).width > maxW) cut--;
    lines[lines.length - 1] = `${last.slice(0, cut)}…`;
  }
  return lines;
}

/**
 * One player's overhead chat line. `show` replaces any live bubble (newest
 * message wins); the bubble self-expires on a TTL scaled by length, fading its
 * last 500 ms, and disposes its texture. `place` re-parents a live bubble when
 * the speaker switches hull↔foot scale.
 */
export class OverheadBubble {
  /** Live bubble text (probes); null once expired. */
  text: string | null = null;
  private sprite: THREE.Sprite | null = null;
  private raf: number | null = null;

  show(text: string, parent: THREE.Object3D, width: number, baseY: number): void {
    this.clear();
    const canvas = document.createElement("canvas");
    const meas = canvas.getContext("2d")!;
    meas.font = `${BUB_FONT}px "Helvetica Neue", system-ui, sans-serif`;
    const lines = wrapBubble(meas, text, BUB_W - BUB_PAD * 2);
    if (lines.length === 0) return;
    canvas.width = BUB_W;
    canvas.height = BUB_PAD * 2 + BUB_LINE * lines.length;
    const ctx = canvas.getContext("2d")!;
    ctx.font = `${BUB_FONT}px "Helvetica Neue", system-ui, sans-serif`;
    ctx.textAlign = "center";
    const wide = Math.min(
      BUB_W,
      Math.max(...lines.map((l) => ctx.measureText(l).width)) + BUB_PAD * 2,
    );
    ctx.fillStyle = "rgba(3, 4, 9, 0.72)";
    ctx.beginPath();
    ctx.roundRect((BUB_W - wide) / 2, 0, wide, canvas.height, 10);
    ctx.fill();
    ctx.fillStyle = "#eef4ff";
    for (let i = 0; i < lines.length; i++) {
      ctx.fillText(lines[i], BUB_W / 2, BUB_PAD + BUB_LINE * i + BUB_FONT);
    }
    const sprite = new THREE.Sprite(
      new THREE.SpriteMaterial({
        map: new THREE.CanvasTexture(canvas),
        transparent: true,
        depthWrite: false,
      }),
    );
    const h = width * (canvas.height / BUB_W);
    sprite.scale.set(width, h, 1);
    sprite.position.set(0, baseY + h / 2, 0);
    parent.add(sprite);
    this.sprite = sprite;
    this.text = text;
    const ttl = 4000 + Math.min(3000, text.length * 40);
    const born = performance.now();
    const tick = () => {
      const left = born + ttl - performance.now();
      if (left <= 0 || !this.sprite) { this.clear(); return; }
      if (left < 500) this.sprite.material.opacity = left / 500;
      this.raf = requestAnimationFrame(tick);
    };
    this.raf = requestAnimationFrame(tick);
  }

  /** Move a live bubble to a new parent/scale (hull↔foot switch mid-life). */
  place(parent: THREE.Object3D, width: number, baseY: number): void {
    if (!this.sprite) return;
    const h = width * (this.sprite.scale.y / this.sprite.scale.x);
    this.sprite.scale.set(width, h, 1);
    this.sprite.position.set(0, baseY + h / 2, 0);
    parent.add(this.sprite); // add() re-parents
  }

  clear(): void {
    if (this.raf !== null) cancelAnimationFrame(this.raf);
    this.raf = null;
    if (this.sprite) {
      this.sprite.parent?.remove(this.sprite);
      this.sprite.material.map?.dispose();
      this.sprite.material.dispose();
    }
    this.sprite = null;
    this.text = null;
  }
}

/** Floating name label rendered to a canvas sprite. */
export function makeNameSprite(name: string, tag?: string): THREE.Sprite {
  const canvas = document.createElement("canvas");
  canvas.width = 256;
  canvas.height = 64;
  const ctx = canvas.getContext("2d")!;
  ctx.font = "28px monospace";
  ctx.textAlign = "center";
  // `[TAG] Name`: the tag is drawn in the GUILD strand (the same amethyst the
  // guild tab and guild chat wear) ahead of the name, on one shared plate, so
  // the badge reads as part of the same label.
  const badge = tag ? `[${tag}] ` : "";
  const badgeW = badge ? ctx.measureText(badge).width : 0;
  const nameW = ctx.measureText(name).width;
  ctx.fillStyle = "rgba(3, 4, 9, 0.62)";
  const w = badgeW + nameW + 24;
  ctx.fillRect(128 - w / 2, 8, w, 44);
  if (badge) {
    ctx.textAlign = "left";
    ctx.fillStyle = strand("guild");
    ctx.fillText(badge, 128 - w / 2 + 12, 40);
    ctx.fillStyle = "#cfe8ff";
    ctx.fillText(name, 128 - w / 2 + 12 + badgeW, 40);
  } else {
    ctx.fillStyle = "#cfe8ff";
    ctx.fillText(name, 128, 40);
  }
  const sprite = new THREE.Sprite(
    new THREE.SpriteMaterial({
      map: new THREE.CanvasTexture(canvas),
      transparent: true,
      depthWrite: false,
    }),
  );
  sprite.scale.set(12, 3, 1);
  sprite.position.set(0, 3.4, 0);
  return sprite;
}
